import React, { useEffect, useMemo, useState } from "react";
import { QrCode, Plus, Store, Package, Home } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuth } from "../context/AuthProvider.tsx";
import QRCode from "qrcode";
import { generarQrDinamico, getComerciosResumen } from "../services/puntosAdminService.tsx";
import {
  createComercioMarket,
  createProductoMarket,
  getComerciosMarket,
  getProductosMarket,
  subirImagenProductoMarket,
  sugerirProductoIaEmprendedor,
  updateProductoMarket,
} from "../services/marketplaceService.tsx";

type AdminSocio = { socio_id: number; nombre: string; mail?: string | null };
type Comercio = {
  comercio_id: number;
  nombre: string;
  activo: boolean;
  puntos_por_carga: number;
  logo_url?: string | null;
  totalPuntos?: number;
  totalScans?: number;
  admins?: AdminSocio[];
};

const norm = (v: string) =>
  String(v || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

export default function MisComerciosSocio() {
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin } = useAuth();
  const [socioId, setSocioId] = useState<number | null>(null);
  const [comercios, setComercios] = useState<Comercio[]>([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");

  const [showQr, setShowQr] = useState(false);
  const [selected, setSelected] = useState<Comercio | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [qrPayload, setQrPayload] = useState("");
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [generandoQr, setGenerandoQr] = useState(false);
  const [tick, setTick] = useState(0);

  const [showProducto, setShowProducto] = useState(false);
  const [producto, setProducto] = useState({ nombre: "", descripcion: "", precio: 0, stock: 0 });
  const [imagenProductoFile, setImagenProductoFile] = useState<File | null>(null);
  const [guardandoProducto, setGuardandoProducto] = useState(false);
  const [sugiriendoIa, setSugiriendoIa] = useState(false);
  const [showProductosLista, setShowProductosLista] = useState(false);
  const [productosComercio, setProductosComercio] = useState<any[]>([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [savingProductoId, setSavingProductoId] = useState<number | null>(null);
  const [showEditarProducto, setShowEditarProducto] = useState(false);
  const [editProducto, setEditProducto] = useState<any>(null);
  const [showDetalleComercio, setShowDetalleComercio] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const socioSel = localStorage.getItem("socioSeleccionado");
      const socioParsed = socioSel ? JSON.parse(socioSel) : null;
      const sid = Number(socioParsed?.socio_id);
      if (!Number.isFinite(sid) || sid <= 0) {
        setSocioId(null);
        setComercios([]);
        return;
      }
      setSocioId(sid);

      const token = await getAccessTokenSilently();
      const data = (await getComerciosResumen(token)) as Comercio[];
      const todos = Array.isArray(data) ? data : [];
      setComercios(todos);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  useEffect(() => {
    const run = async () => {
      if (!qrPayload) return setQrDataUrl("");
      setQrDataUrl(await QRCode.toDataURL(qrPayload, { width: 700, margin: 1 }));
    };
    run();
  }, [qrPayload]);

  useEffect(() => {
    if (!showQr || !selected?.activo || !expiresAt) return;
    const id = setInterval(() => {
      setTick((t) => t + 1);
      const remaining = expiresAt - Date.now();
      if (remaining <= 0 && !generandoQr) {
        generarQr();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [showQr, selected, expiresAt, generandoQr]);

  const generarQr = async () => {
    if (!selected?.comercio_id || !selected?.activo) return;
    try {
      setGenerandoQr(true);
      const token = await getAccessTokenSilently();
      const resp = await generarQrDinamico(selected.comercio_id, token, 60);
      setQrPayload(resp.token_qr || "");
      setExpiresAt(resp.expires_at ? new Date(resp.expires_at).getTime() : null);
    } finally {
      setGenerandoQr(false);
    }
  };

  const abrirQr = async (c: Comercio) => {
    setSelected(c);
    setShowQr(true);
    setQrPayload("");
    setExpiresAt(null);
    if (c.activo) await generarQrInterno(c.comercio_id);
  };

  const generarQrInterno = async (comercioId: number) => {
    try {
      setGenerandoQr(true);
      const token = await getAccessTokenSilently();
      const resp = await generarQrDinamico(comercioId, token, 60);
      setQrPayload(resp.token_qr || "");
      setExpiresAt(resp.expires_at ? new Date(resp.expires_at).getTime() : null);
    } finally {
      setGenerandoQr(false);
    }
  };

  const segundos = useMemo(() => (expiresAt ? Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)) : 0), [expiresAt, tick]);

  const comerciosVisibles = useMemo(() => {
    const base = comercios.filter(
      (c) => Array.isArray(c.admins) && c.admins.some((a) => Number(a?.socio_id) === Number(socioId))
    );

    const term = norm(busqueda);
    if (!term) return base;

    return base.filter((c) => {
      const comercioMatch = norm(c.nombre).includes(term);
      const socioMatch = (c.admins || []).some((a) => {
        const nombre = norm(a?.nombre || "");
        const mail = norm(a?.mail || "");
        const id = String(a?.socio_id || "");
        return nombre.includes(term) || mail.includes(term) || id.includes(term);
      });
      return comercioMatch || socioMatch;
    });
  }, [comercios, socioId, busqueda]);

  const abrirDetalleComercio = (c: Comercio) => {
    setSelected(c);
    setShowDetalleComercio(true);
  };

  const abrirCargaProducto = (c: Comercio) => {
    setSelected(c);
    setProducto({ nombre: "", descripcion: "", precio: 0, stock: 0 });
    setImagenProductoFile(null);
    setShowProducto(true);
  };

  const cargarProductosDeComercio = async (c: Comercio) => {
    const market = await getComerciosMarket();
    const existente = (market || []).find((m) => norm(m.nombre) === norm(c.nombre));
    if (!existente?.comercio_id) {
      setProductosComercio([]);
      return;
    }
    const items = await getProductosMarket(Number(existente.comercio_id), true);
    setProductosComercio(Array.isArray(items) ? items : []);
  };

  const abrirProductos = async (c: Comercio) => {
    setSelected(c);
    setShowProductosLista(true);
    setLoadingProductos(true);
    try {
      await cargarProductosDeComercio(c);
    } catch (e) {
      console.error(e);
      setProductosComercio([]);
    } finally {
      setLoadingProductos(false);
    }
  };

  const sugerirProductoIa = async () => {
    if (!selected) return;
    try {
      setSugiriendoIa(true);
      const sug = await sugerirProductoIaEmprendedor({
        nombre_marca: selected.nombre,
        rubro: 'general',
        foto_nombre: imagenProductoFile?.name,
        imageFile: imagenProductoFile,
      });
      setProducto((p) => ({
        ...p,
        nombre: String(sug?.titulo || p.nombre || '').trim(),
        descripcion: String(sug?.descripcion || p.descripcion || '').trim(),
        precio: Number(sug?.precio_sugerido || p.precio || 0),
      }));
    } catch (e) {
      console.error(e);
      alert('No se pudo generar sugerencia con IA.');
    } finally {
      setSugiriendoIa(false);
    }
  };

  const guardarProducto = async () => {
    if (!selected || !socioId) return;
    if (!producto.nombre.trim()) return;

    try {
      setGuardandoProducto(true);
      const market = await getComerciosMarket();
      const existente = (market || []).find((m) => norm(m.nombre) === norm(selected.nombre));

      const comercioMarket = existente
        ? existente
        : await createComercioMarket({
            nombre: selected.nombre,
            socio_id: socioId,
            descripcion: `Comercio asociado #${selected.comercio_id}`,
            activo: true,
          });

      const created = await createProductoMarket({
        comercio_id: Number((comercioMarket as any).comercio_id),
        nombre: producto.nombre.trim(),
        descripcion: producto.descripcion || undefined,
        precio: Number(producto.precio || 0),
        stock: Number(producto.stock || 0),
        activo: true,
      });

      if (imagenProductoFile && (created as any)?.producto_id) {
        const token = await getAccessTokenSilently();
        await subirImagenProductoMarket(Number((created as any).producto_id), imagenProductoFile, token);
      }

      setShowProducto(false);
      await cargarProductosDeComercio(selected);
      alert("Producto cargado correctamente.");
    } catch (e) {
      console.error(e);
      alert("No se pudo cargar el producto.");
    } finally {
      setGuardandoProducto(false);
    }
  };

  const toggleProductoActivo = async (p: any) => {
    if (!selected) return;
    try {
      setSavingProductoId(Number(p.producto_id));
      await updateProductoMarket(Number(p.producto_id), { activo: !p.activo });
      await cargarProductosDeComercio(selected);
    } finally {
      setSavingProductoId(null);
    }
  };

  const guardarEdicionProducto = async () => {
    if (!selected || !editProducto?.producto_id) return;
    try {
      setSavingProductoId(Number(editProducto.producto_id));
      await updateProductoMarket(Number(editProducto.producto_id), {
        nombre: String(editProducto.nombre || '').trim(),
        descripcion: editProducto.descripcion || '',
        precio: Number(editProducto.precio || 0),
        stock: Number(editProducto.stock || 0),
      });
      setShowEditarProducto(false);
      setEditProducto(null);
      await cargarProductosDeComercio(selected);
    } finally {
      setSavingProductoId(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-4 space-y-4">
      <nav className="flex" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/inicio" className="inline-flex text-sm font-medium text-blue-600 hover:text-blue-800">
              <Home className="w-4 h-4 mr-2" />
              Inicio
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <Store className="w-4 h-4 text-gray-400" />
              <span className="ml-1 text-sm font-medium text-gray-600 md:ml-2">Mis comercios administrados</span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="rounded-xl bg-white p-4 shadow space-y-3">
        <h1 className="text-2xl font-bold">Mis comercios administrados</h1>
        <p className="text-sm text-slate-600">
          {isAdmin
            ? "Vista administrador: hacé click en cada registro para ver detalle y gestionar el comercio."
            : "Acá ves los comercios que administra tu socio. Hacé click en un registro para ver el detalle."}
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <input
            className="w-full sm:max-w-md rounded border p-2"
            placeholder={isAdmin ? "Buscar por comercio, socio, mail o ID de socio" : "Buscar comercio"}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <span className="text-sm text-slate-500">{comerciosVisibles.length} comercio(s)</span>
        </div>
      </div>

      <div className="rounded-xl bg-white p-4 shadow overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b text-left text-slate-500">
              <th className="py-2">Comercio</th>
              {isAdmin && <th className="py-2">Socios asociados</th>}
              <th className="py-2">Estado QR</th>
              <th className="py-2">Puntos/carga</th>
              <th className="py-2">Puntos otorgados</th>
              <th className="py-2">Escaneos</th>
              <th className="py-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 4 }).map((_, idx) => (
                  <tr key={`sk-${idx}`} className="border-b animate-pulse">
                    <td className="py-2"><div className="h-4 w-40 rounded bg-slate-200" /></td>
                    {isAdmin && <td className="py-2"><div className="h-4 w-48 rounded bg-slate-200" /></td>}
                    <td className="py-2"><div className="h-6 w-20 rounded bg-slate-200" /></td>
                    <td className="py-2"><div className="h-4 w-14 rounded bg-slate-200" /></td>
                    <td className="py-2"><div className="h-4 w-20 rounded bg-slate-200" /></td>
                    <td className="py-2"><div className="h-4 w-14 rounded bg-slate-200" /></td>
                    <td className="py-2">
                      <div className="flex gap-2">
                        <div className="h-8 w-24 rounded bg-slate-200" />
                        <div className="h-8 w-24 rounded bg-slate-200" />
                        <div className="h-8 w-24 rounded bg-slate-200" />
                      </div>
                    </td>
                  </tr>
                ))
              : comerciosVisibles.map((c) => (
                  <tr
                    key={c.comercio_id}
                    className="border-b cursor-pointer hover:bg-slate-50"
                    onClick={() => abrirDetalleComercio(c)}
                  >
                    <td className="py-2 font-medium">{c.nombre}</td>
                    {isAdmin && (
                      <td className="py-2 text-xs text-slate-600">
                        {(c.admins || []).length
                          ? (c.admins || []).map((a) => `${a.nombre || `Socio #${a.socio_id}`} (#${a.socio_id})`).join(", ")
                          : "Sin socios asociados"}
                      </td>
                    )}
                    <td className="py-2">
                      <span className={`rounded px-2 py-1 text-xs font-semibold ${c.activo ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}`}>
                        {c.activo ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="py-2">+{c.puntos_por_carga}</td>
                    <td className="py-2 font-semibold">{Number(c.totalPuntos || 0).toLocaleString("es-AR")}</td>
                    <td className="py-2">{Number(c.totalScans || 0).toLocaleString("es-AR")}</td>
                    <td className="py-2">
                      <div className="flex gap-2 flex-wrap">
                        <button
                          className="rounded border px-2 py-1 inline-flex items-center gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirDetalleComercio(c);
                          }}
                        >
                          Ver detalle
                        </button>
                        <button
                          className="rounded bg-slate-800 px-2 py-1 text-white inline-flex items-center gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirQr(c);
                          }}
                        >
                          <QrCode className="h-4 w-4" /> QR dinámico
                        </button>
                        <button
                          className="rounded border px-2 py-1 inline-flex items-center gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirProductos(c);
                          }}
                        >
                          <Package className="h-4 w-4" /> Ver productos
                        </button>
                        <button
                          className="rounded border px-2 py-1 inline-flex items-center gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirCargaProducto(c);
                          }}
                        >
                          <Plus className="h-4 w-4" /> Cargar producto
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

            {!loading && comerciosVisibles.length === 0 && (
              <tr>
                <td colSpan={isAdmin ? 7 : 6} className="py-4 text-center text-slate-500">
                  {isAdmin ? "No hay comercios para mostrar." : "No tenés comercios asociados como admin todavía."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showDetalleComercio && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white p-4 shadow-xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Detalle del comercio</h3>
              <button className="rounded border px-3 py-1" onClick={() => setShowDetalleComercio(false)}>Cerrar</button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-slate-500">Nombre</p>
                <p className="font-semibold text-slate-800">{selected.nombre}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-slate-500">ID comercio</p>
                <p className="font-semibold text-slate-800">#{selected.comercio_id}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-slate-500">Estado</p>
                <p className="font-semibold text-slate-800">{selected.activo ? 'Activo' : 'Inactivo'}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-slate-500">Puntos por carga</p>
                <p className="font-semibold text-slate-800">+{selected.puntos_por_carga}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-slate-500">Puntos otorgados</p>
                <p className="font-semibold text-slate-800">{Number(selected.totalPuntos || 0).toLocaleString('es-AR')}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-slate-500">Escaneos</p>
                <p className="font-semibold text-slate-800">{Number(selected.totalScans || 0).toLocaleString('es-AR')}</p>
              </div>
            </div>

            <div className="rounded-lg border p-3">
              <p className="mb-1 text-xs text-slate-500">Socios administradores</p>
              {(selected.admins || []).length ? (
                <ul className="list-disc pl-5 text-sm text-slate-700">
                  {(selected.admins || []).map((a) => (
                    <li key={`${selected.comercio_id}-${a.socio_id}`}>{a.nombre || `Socio #${a.socio_id}`} (#{a.socio_id})</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-500">Sin socios asociados.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {showQr && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-xl bg-white p-4 shadow-xl space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">QR dinámico · {selected.nombre}</h3>
              <button className="rounded border px-3 py-1" onClick={() => setShowQr(false)}>Cerrar</button>
            </div>
            <div className="flex justify-center rounded border border-sky-200 bg-sky-50 p-3">
              <div className="flex h-64 w-64 items-center justify-center rounded bg-white p-2">
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt="QR dinámico" className="h-full w-full rounded object-contain" />
                ) : selected.activo ? (
                  <div className="h-full w-full animate-pulse rounded bg-slate-200" />
                ) : (
                  <p className="px-4 text-center text-sm text-slate-500">Comercio inactivo</p>
                )}
              </div>
            </div>
            {selected.activo && (
              <div className="rounded-md bg-sky-50 px-3 py-2 text-sm text-sky-800">
                Vigencia: <b>{segundos}s</b>. Se renueva automáticamente.
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button className="rounded border px-3 py-2" onClick={() => setShowQr(false)}>Cerrar</button>
              <button className="rounded bg-sky-700 px-3 py-2 text-white disabled:opacity-50" onClick={generarQr} disabled={!selected.activo || generandoQr}>
                {generandoQr ? "Generando..." : "Regenerar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showProductosLista && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-xl bg-white p-4 shadow-xl space-y-3 max-h-[85vh] overflow-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold inline-flex items-center gap-2"><Package className="h-5 w-5" /> Productos · {selected.nombre}</h3>
              <button className="rounded border px-3 py-1" onClick={() => setShowProductosLista(false)}>Cerrar</button>
            </div>

            {loadingProductos ? (
              <p className="text-sm text-slate-500">Cargando productos...</p>
            ) : productosComercio.length === 0 ? (
              <p className="rounded border border-dashed p-4 text-sm text-slate-500">Este comercio no tiene productos cargados en marketplace.</p>
            ) : (
              <div className="overflow-x-auto rounded border">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Producto</th>
                      <th className="px-3 py-2 text-left">Precio</th>
                      <th className="px-3 py-2 text-left">Stock</th>
                      <th className="px-3 py-2 text-left">Estado</th>
                      <th className="px-3 py-2 text-left">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productosComercio.map((p) => (
                      <tr key={p.producto_id} className="border-t">
                        <td className="px-3 py-2">
                          <p className="font-medium">{p.nombre}</p>
                          {p.descripcion ? <p className="text-xs text-slate-500">{p.descripcion}</p> : null}
                        </td>
                        <td className="px-3 py-2">${Number(p.precio || 0).toLocaleString("es-AR")}</td>
                        <td className="px-3 py-2">{Number(p.stock || 0)}</td>
                        <td className="px-3 py-2">{p.activo ? "Activo" : "Inactivo"}</td>
                        <td className="px-3 py-2">
                          <div className="flex gap-2">
                            <button
                              className="rounded border px-2 py-1 text-xs"
                              onClick={() => {
                                setEditProducto({ ...p });
                                setShowEditarProducto(true);
                              }}
                              disabled={savingProductoId === Number(p.producto_id)}
                            >
                              Editar
                            </button>
                            <button
                              className={`rounded px-2 py-1 text-xs text-white ${p.activo ? "bg-amber-600" : "bg-emerald-600"}`}
                              onClick={() => toggleProductoActivo(p)}
                              disabled={savingProductoId === Number(p.producto_id)}
                            >
                              {savingProductoId === Number(p.producto_id)
                                ? "Guardando..."
                                : p.activo
                                ? "Desactivar"
                                : "Activar"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {showEditarProducto && editProducto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-xl bg-white p-4 shadow-xl space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Editar producto</h3>
              <button className="rounded border px-3 py-1" onClick={() => setShowEditarProducto(false)}>Cerrar</button>
            </div>
            <input className="w-full rounded border p-2" placeholder="Nombre" value={editProducto.nombre || ''} onChange={(e) => setEditProducto((p: any) => ({ ...p, nombre: e.target.value }))} />
            <textarea className="w-full rounded border p-2" rows={3} placeholder="Descripción" value={editProducto.descripcion || ''} onChange={(e) => setEditProducto((p: any) => ({ ...p, descripcion: e.target.value }))} />
            <div className="grid grid-cols-2 gap-2">
              <input className="w-full rounded border p-2" type="number" placeholder="Precio" value={editProducto.precio || 0} onChange={(e) => setEditProducto((p: any) => ({ ...p, precio: Number(e.target.value || 0) }))} />
              <input className="w-full rounded border p-2" type="number" placeholder="Stock" value={editProducto.stock || 0} onChange={(e) => setEditProducto((p: any) => ({ ...p, stock: Number(e.target.value || 0) }))} />
            </div>
            <div className="flex justify-end gap-2">
              <button className="rounded border px-3 py-2" onClick={() => setShowEditarProducto(false)}>Cancelar</button>
              <button className="rounded bg-blue-600 px-3 py-2 text-white disabled:opacity-50" onClick={guardarEdicionProducto} disabled={savingProductoId === Number(editProducto.producto_id)}>
                {savingProductoId === Number(editProducto.producto_id) ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showProducto && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-xl bg-white p-4 shadow-xl space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold inline-flex items-center gap-2"><Store className="h-5 w-5" /> Cargar producto · {selected.nombre}</h3>
              <button className="rounded border px-3 py-1" onClick={() => setShowProducto(false)}>Cerrar</button>
            </div>
            <input className="w-full rounded border p-2" placeholder="Nombre" value={producto.nombre} onChange={(e) => setProducto((p) => ({ ...p, nombre: e.target.value }))} />
            <textarea className="w-full rounded border p-2" rows={3} placeholder="Descripción" value={producto.descripcion} onChange={(e) => setProducto((p) => ({ ...p, descripcion: e.target.value }))} />
            <div className="grid grid-cols-2 gap-2">
              <input className="w-full rounded border p-2" type="number" placeholder="Precio" value={producto.precio} onChange={(e) => setProducto((p) => ({ ...p, precio: Number(e.target.value || 0) }))} />
              <input className="w-full rounded border p-2" type="number" placeholder="Stock" value={producto.stock} onChange={(e) => setProducto((p) => ({ ...p, stock: Number(e.target.value || 0) }))} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Imagen del producto (se sube a storage)</label>
              <input
                className="w-full rounded border p-2"
                type="file"
                accept="image/*"
                onChange={(e) => setImagenProductoFile(e.target.files?.[0] || null)}
              />
              <p className="text-xs text-slate-500">Tip: cargá una foto clara para que IA complete título, descripción y precio estimado.</p>
            </div>

            <div className="flex justify-between gap-2">
              <button
                className="rounded border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 disabled:opacity-50"
                onClick={sugerirProductoIa}
                disabled={sugiriendoIa}
              >
                {sugiriendoIa ? 'Analizando imagen...' : 'Autocompletar con IA'}
              </button>
              <div className="flex gap-2">
              <button className="rounded border px-3 py-2" onClick={() => setShowProducto(false)}>Cancelar</button>
              <button className="rounded bg-blue-600 px-3 py-2 text-white disabled:opacity-50" onClick={guardarProducto} disabled={guardandoProducto || !producto.nombre.trim()}>
                {guardandoProducto ? "Guardando..." : "Guardar producto"}
              </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
